import type {
  ConflictRecordV1,
  ContentConflictReason,
  ContentMutationV1,
  DurablePendingContentTupleV1,
  LocalContentEntityV1,
  OrdinaryContentEnvelopeV1,
  StagedConflictV1,
} from '@/types/content-lifecycle';
import { cj1, decodeCj1Bytes, envelopeBytesEqual, projectMutation } from './contracts';

export type InstallResult = {
  entity: LocalContentEntityV1;
  conflict: ConflictRecordV1 | null;
};

export function mutationFromPending(
  pending: DurablePendingContentTupleV1,
): ContentMutationV1 {
  return decodeCj1Bytes(pending.completePendingBytes) as ContentMutationV1;
}

export function envelopeWinningKind(
  envelope: OrdinaryContentEnvelopeV1,
): 'live' | 'tombstone' {
  return envelope.payload.kind;
}

function conflictReasonFor(
  loser: ContentMutationV1 | OrdinaryContentEnvelopeV1,
  winner: OrdinaryContentEnvelopeV1,
): ContentConflictReason {
  const loserKind = loser.payload.kind === 'live' ? 'live' : 'tombstone';
  const winnerKind = envelopeWinningKind(winner);
  if (loserKind === 'live' && winnerKind === 'tombstone') return 'live-vs-tombstone';
  if (loserKind === 'tombstone' && winnerKind === 'live') return 'tombstone-vs-live';
  return 'cas-mismatch';
}

function sameTombstoneMeaning(
  loser: ContentMutationV1 | OrdinaryContentEnvelopeV1,
  winner: OrdinaryContentEnvelopeV1,
): boolean {
  if (loser.payload.kind === 'live' || winner.payload.kind !== 'tombstone') return false;
  const loserPayload = loser.payload;
  return (
    loserPayload.predecessorLiveDigest === winner.payload.predecessorLiveDigest &&
    cj1(loserPayload.predecessorLiveSnapshot) === cj1(winner.payload.predecessorLiveSnapshot)
  );
}

function pendingConflict(
  pending: DurablePendingContentTupleV1,
  winner: OrdinaryContentEnvelopeV1,
  createdAt: string,
  rejection: {
    source: 'cas-rejection' | 'pull-observation';
    selection: 'rejected-mutation' | 'newer-local-successor' | 'pull-observation';
    rejectedExpectedBaseRevision: number | null;
    rejectedOpId: string | null;
    rejectedMutationSeq: string | null;
  },
): ConflictRecordV1 {
  const mutation = mutationFromPending(pending);
  return {
    recordVersion: 1,
    ownerId: winner.ownerId,
    entityType: winner.entityType,
    entityId: winner.entityId,
    conflictReason: conflictReasonFor(mutation, winner),
    winningKind: envelopeWinningKind(winner),
    winningContentRevision: winner.contentRevision,
    winningContentDigest: winner.contentDigest,
    losingSource: 'pending',
    losingProtocolVersion: 1,
    losingOpKind: mutation.opKind,
    losingOpId: mutation.opId,
    losingMutationSeq: mutation.mutationSeq,
    losingExpectedBaseRevision: mutation.expectedBaseRevision,
    losingContentRevision: null,
    loserSelection: rejection.selection,
    losingPayload: mutation.payload,
    losingContentDigest: pending.contentDigest,
    rejectionSource: rejection.source,
    rejectedExpectedBaseRevision: rejection.rejectedExpectedBaseRevision,
    rejectedOpId: rejection.rejectedOpId,
    rejectedMutationSeq: rejection.rejectedMutationSeq,
    createdAt,
  };
}

function acceptedConflict(
  loser: OrdinaryContentEnvelopeV1,
  winner: OrdinaryContentEnvelopeV1,
  createdAt: string,
): ConflictRecordV1 {
  return {
    recordVersion: 1,
    ownerId: winner.ownerId,
    entityType: winner.entityType,
    entityId: winner.entityId,
    conflictReason: conflictReasonFor(loser, winner),
    winningKind: envelopeWinningKind(winner),
    winningContentRevision: winner.contentRevision,
    winningContentDigest: winner.contentDigest,
    losingSource: 'accepted',
    losingProtocolVersion: 1,
    losingOpKind: loser.opKind,
    losingOpId: loser.opId,
    losingMutationSeq: loser.mutationSeq,
    losingExpectedBaseRevision: loser.expectedBaseRevision,
    losingContentRevision: loser.contentRevision,
    loserSelection: 'pull-observation',
    losingPayload: loser.payload,
    losingContentDigest: loser.contentDigest,
    rejectionSource: 'pull-observation',
    rejectedExpectedBaseRevision: null,
    rejectedOpId: null,
    rejectedMutationSeq: null,
    createdAt,
  };
}

function installedEntity(
  prior: LocalContentEntityV1 | null,
  winner: OrdinaryContentEnvelopeV1,
  conflict: ConflictRecordV1 | null,
): LocalContentEntityV1 {
  return {
    ownerId: winner.ownerId,
    entityType: winner.entityType,
    entityId: winner.entityId,
    state: conflict
      ? 'conflicted'
      : winner.payload.kind === 'tombstone'
        ? 'tombstoned'
        : prior?.pending
          ? 'dirty'
          : 'clean',
    installedEnvelope: winner,
    visibleDocument: winner.payload.kind === 'live' ? winner.payload.document : null,
    pending: conflict ? null : (prior?.pending ?? null),
    pendingDeleteAt: null,
    mutationSeq: prior?.mutationSeq ?? winner.mutationSeq,
    localBaseRevision: winner.contentRevision,
    heldReason: null,
    quarantineReason: null,
    recovery: conflict
      ? 'conflict-keep-both'
      : winner.payload.kind === 'tombstone'
        ? 'tombstone-installed'
        : 'installed-remote',
  };
}

/**
 * Pure contiguous-install cell. The caller owns validation, exact replay
 * checks, owner/auth/generation guards, and the surrounding IDB transaction.
 */
export function installContiguousEnvelope(
  prior: LocalContentEntityV1 | null,
  winner: OrdinaryContentEnvelopeV1,
  activeConflict: ConflictRecordV1 | null,
  stagedConflict: StagedConflictV1 | null,
  createdAt: string,
): InstallResult {
  if (activeConflict) {
    const advanced: ConflictRecordV1 = {
      ...activeConflict,
      winningKind: envelopeWinningKind(winner),
      winningContentRevision: winner.contentRevision,
      winningContentDigest: winner.contentDigest,
    };
    return { entity: installedEntity(prior, winner, advanced), conflict: advanced };
  }

  if (stagedConflict) {
    const selected = stagedConflict.supersedingMutation ?? stagedConflict.rejectedMutation;
    const selectedDigest =
      stagedConflict.supersedingContentDigest ?? stagedConflict.rejectedContentDigest;
    const pending: DurablePendingContentTupleV1 = {
      ownerId: selected.ownerId,
      authEpoch: stagedConflict.authEpoch,
      generation: stagedConflict.generation,
      entityType: selected.entityType,
      entityId: selected.entityId,
      opKind: selected.opKind,
      mutationSeq: selected.mutationSeq,
      opId: selected.opId,
      completePendingBytes: new TextEncoder().encode(cj1(selected)),
      contentDigest: selectedDigest,
      expectedBaseRevision: selected.expectedBaseRevision,
    };
    const conflict = pendingConflict(pending, winner, createdAt, {
      source: 'cas-rejection',
      selection: stagedConflict.supersedingMutation
        ? 'newer-local-successor'
        : 'rejected-mutation',
      rejectedExpectedBaseRevision: stagedConflict.rejectedExpectedBaseRevision,
      rejectedOpId: stagedConflict.rejectedOpId,
      rejectedMutationSeq: stagedConflict.rejectedMutationSeq,
    });
    return { entity: installedEntity(prior, winner, conflict), conflict };
  }

  // An exact receipt may install this accepted local predecessor before its
  // log revision becomes contiguous. The newer local tuple has already been
  // re-frozen against that exact revision; the later pull is a replay of the
  // causal base, not a competing remote edit. Preserve the successor bytes and
  // visible document rather than manufacturing a conflict or UI rollback.
  if (
    prior?.pending &&
    prior.installedEnvelope &&
    prior.pending.expectedBaseRevision === winner.contentRevision &&
    envelopeBytesEqual(prior.installedEnvelope, winner)
  ) {
    return {
      entity: {
        ...prior,
        installedEnvelope: winner,
        localBaseRevision: winner.contentRevision,
        heldReason: null,
        quarantineReason: null,
        recovery: 'upload-acked',
      },
      conflict: null,
    };
  }

  const pending = prior?.pending ?? null;
  if (pending) {
    const mutation = mutationFromPending(pending);
    if (
      mutation.opId === winner.opId &&
      pending.contentDigest === winner.contentDigest &&
      cj1(mutation) === cj1(projectMutation(winner))
    ) {
      return { entity: installedEntity(prior, winner, null), conflict: null };
    }
    if (sameTombstoneMeaning(mutation, winner)) {
      return {
        entity: installedEntity({ ...(prior as LocalContentEntityV1), pending: null }, winner, null),
        conflict: null,
      };
    }
    const conflict = pendingConflict(pending, winner, createdAt, {
      source: 'pull-observation',
      selection: 'pull-observation',
      rejectedExpectedBaseRevision: null,
      rejectedOpId: null,
      rejectedMutationSeq: null,
    });
    return { entity: installedEntity(prior, winner, conflict), conflict };
  }

  if (
    prior?.state === 'tombstoned' &&
    prior.installedEnvelope?.payload.kind === 'tombstone' &&
    winner.payload.kind === 'live'
  ) {
    const conflict = acceptedConflict(prior.installedEnvelope, winner, createdAt);
    return { entity: installedEntity(prior, winner, conflict), conflict };
  }

  if (
    prior?.state === 'tombstoned' &&
    prior.installedEnvelope?.payload.kind === 'tombstone' &&
    winner.payload.kind === 'tombstone' &&
    !sameTombstoneMeaning(prior.installedEnvelope, winner)
  ) {
    const conflict = acceptedConflict(prior.installedEnvelope, winner, createdAt);
    return { entity: installedEntity(prior, winner, conflict), conflict };
  }

  return { entity: installedEntity(prior, winner, null), conflict: null };
}

export function restoreLoserOperationKind(
  record: ConflictRecordV1,
): 'update' | 'restore' | 'delete' | 'keep-remote' {
  const loserKind = record.losingPayload.kind === 'live' ? 'live' : 'tombstone';
  if (loserKind === 'live' && record.winningKind === 'live') return 'update';
  if (loserKind === 'live' && record.winningKind === 'tombstone') return 'restore';
  if (loserKind === 'tombstone' && record.winningKind === 'live') return 'delete';
  return 'keep-remote';
}
